/**
 * The onboarding domain's client-side vocabulary — the label maps and the one piece of
 * derived arithmetic the checklist surface needs (a task's done/total **progress**).
 *
 * Nothing here decides anything about a request. The labels turn the contract's enums
 * into `MessageKey`s so the screen never hard-codes a status word, and `checklistProgress`
 * counts the tasks the server already sent — it is a rendering aid, not a second source
 * of truth about a checklist's state (the server owns `status`, and the screen shows
 * both the server's status badge and this count).
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type {
  OnboardingCategory,
  OnboardingChecklist,
  OnboardingChecklistStatus,
  OnboardingTaskStatus,
} from '../api/hr';

/** The name of an item's / task's `category`, for the template and checklist lists. */
export const ONBOARDING_CATEGORY_LABEL: Readonly<Record<OnboardingCategory, MessageKey>> = {
  it: 'hr.onboarding.category.it',
  hr: 'hr.onboarding.category.hr',
  manager: 'hr.onboarding.category.manager',
};

/** The name of a task's `status`, for the checklist task rows. */
export const ONBOARDING_TASK_STATUS_LABEL: Readonly<Record<OnboardingTaskStatus, MessageKey>> = {
  pending: 'hr.onboarding.taskStatus.pending',
  done: 'hr.onboarding.taskStatus.done',
  skipped: 'hr.onboarding.taskStatus.skipped',
  blocked: 'hr.onboarding.taskStatus.blocked',
};

/** The badge severity a task status reads as — colour third, never alone (DESIGN.md §4). */
export const ONBOARDING_TASK_STATUS_BADGE: Readonly<Record<OnboardingTaskStatus, Severity>> = {
  pending: 'info',
  done: 'low',
  skipped: 'medium',
  blocked: 'high',
};

/** The name of a checklist's `status`, for the checklist header. */
export const ONBOARDING_CHECKLIST_STATUS_LABEL: Readonly<
  Record<OnboardingChecklistStatus, MessageKey>
> = {
  in_progress: 'hr.onboarding.checklistStatus.in_progress',
  completed: 'hr.onboarding.checklistStatus.completed',
  cancelled: 'hr.onboarding.checklistStatus.cancelled',
};

/** The badge severity a checklist status reads as. */
export const ONBOARDING_CHECKLIST_STATUS_BADGE: Readonly<
  Record<OnboardingChecklistStatus, Severity>
> = {
  in_progress: 'info',
  completed: 'low',
  cancelled: 'medium',
};

/** How many of a checklist's tasks are `done`, out of how many there are. */
export interface ChecklistProgress {
  readonly done: number;
  readonly total: number;
}

/**
 * The done/total of a checklist's nested tasks. A task counts as done only when its
 * `status` is `done`; `skipped` and `blocked` are not done, which is the honest reading
 * of "how much of this onboarding is finished". A checklist with no tasks is `0 / 0`,
 * not an error and not `NaN` — the screen renders that as "no tasks yet".
 */
export function checklistProgress(checklist: OnboardingChecklist): ChecklistProgress {
  const total = checklist.tasks.length;
  const done = checklist.tasks.filter((task) => task.status === 'done').length;
  return { done, total };
}
